import React, { useState, useRef, useEffect } from 'react';
import './AuthModal.css';

const BASE = 'https://agro.atrmart.in/api';

const api = async (endpoint, body) => {
  try {
    const res = await fetch(`${BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let json;
    try { json = await res.json(); }
    catch { json = { status: false, message: `Server returned ${res.status}` }; }
    if (!res.ok) {
      const msg = json.message
        || (json.errors ? Object.values(json.errors).flat()[0] : null)
        || `Error ${res.status}`;
      throw new Error(msg);
    }
    return json;
  } catch (err) {
    if (err instanceof TypeError) {
      throw new Error('Cannot reach server. Make sure Laravel is running: php artisan serve');
    }
    throw err;
  }
};

const AuthModal = ({ isOpen, onClose, onAuthSuccess }) => {
  const [screen, setScreen] = useState('mobile'); 
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['','','','','','']);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [timer, setTimer] = useState(0);

  const refs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timer > 0) { timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000); }
    return () => clearTimeout(timerRef.current);
  }, [timer]);

  useEffect(() => {
    if (!isOpen) return;
    setScreen('mobile'); setMobile(''); setOtp(['','','','','','']);
    setName(''); setEmail(''); setError(''); setDevOtp(''); setTimer(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const stepIdx = screen === 'mobile' ? 0 : screen === 'otp' ? 1 : 2;

  const oChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const n = [...otp]; n[i] = v; setOtp(n);
    if (v && i < 5) refs.current[i+1]?.focus();
  };
  
  const oKey = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i-1]?.focus();
  };

  const doSendOtp = async (mob) => {
    const res = await api('/user/send-otp', { mobile: mob });
    if (res.dev_otp) setDevOtp(res.dev_otp);
    setScreen('otp');
    setTimer(30);
    setTimeout(() => refs.current[0]?.focus(), 100);
  };

  const onCheckMobile = async e => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) { setError('Enter a valid 10-digit mobile number.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await api('/user/check-mobile', { mobile });
      if (res.exists) { await doSendOtp(mobile); }
      else { setScreen('register'); }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const onVerifyOtp = async e => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setError('Enter all 6 OTP digits.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await api('/user/verify-otp', { mobile, otp: code });
      
      // CRITICAL FIX: Ensure keys match what the Home Page (AgroDashboard) expects
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      
      // Safety check for callback
      if (onAuthSuccess) {
        onAuthSuccess(res.user);
      }
      onClose();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const onResend = async () => {
    if (timer > 0 || loading) return;
    setError(''); setLoading(true);
    try {
      setOtp(['','','','','','']);
      await doSendOtp(mobile);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const onRegister = async e => {
    e.preventDefault();
    if (!name.trim()) { setError('Enter your full name.'); return; }
    setError(''); setLoading(true);
    try {
      await api('/user/register', { name: name.trim(), mobile, email: email || undefined });
      await doSendOtp(mobile);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="auth-modal">
        <div className="auth-modal-header">
          <div className="auth-header-brand">
            <div className="auth-header-hex">A</div>
            <span className="auth-header-name">ATR AGRO MART</span>
          </div>
          <h2 className="auth-modal-title">
            {screen==='mobile'?'Sign in to continue':screen==='otp'?'Verify your number':'Create account'}
          </h2>
          <button className="auth-close-btn" onClick={onClose} type="button">×</button>
        </div>

        <div className="auth-modal-body">
          {error && <div className="auth-msg-error">⚠ {error}</div>}

          {screen==='mobile' && (
            <form onSubmit={onCheckMobile}>
              <div className="auth-input-group">
                <label className="auth-label">MOBILE NUMBER</label>
                <div className="auth-mobile-wrap">
                  <span className="auth-prefix">+91</span>
                  <input type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit number"
                    value={mobile} onChange={e=>{setError('');setMobile(e.target.value.replace(/\D/g,''));}} autoFocus/>
                </div>
              </div>
              <button className="auth-submit" type="submit" disabled={loading||mobile.length!==10}>
                {loading?'Checking…':'Continue →'}
              </button>
            </form>
          )}

          {screen==='otp' && (
            <form onSubmit={onVerifyOtp}>
              <div className="auth-input-group">
                <label className="auth-label">ENTER 6-DIGIT OTP</label>
                <div className="auth-otp-row">
                  {otp.map((d,i)=>(
                    <input key={i} ref={el=>refs.current[i]=el}
                      className="auth-otp-box" type="tel" inputMode="numeric" maxLength={1} value={d}
                      onChange={e=>oChange(i,e.target.value)} onKeyDown={e=>oKey(i,e)}/>
                  ))}
                </div>
                <div className="auth-resend-row">
                  {timer>0?`Resend in ${timer}s`:<span className="auth-resend-link" onClick={onResend}>{loading?'Sending…':'Resend OTP'}</span>}
                </div>
              </div>
              <button className="auth-submit" type="submit" disabled={loading||otp.join('').length<6}>
                {loading?'Verifying…':'✓ Verify & Login'}
              </button>
            </form>
          )}

          {screen==='register' && (
            <form onSubmit={onRegister}>
              <div className="auth-input-group">
                <label className="auth-label">FULL NAME *</label>
                <input className="auth-input" type="text" placeholder="Your full name"
                  value={name} onChange={e=>{setError('');setName(e.target.value);}} autoFocus required/>
              </div>
              <div className="auth-input-group">
                <label className="auth-label">EMAIL (optional)</label>
                <input className="auth-input" type="email" placeholder="you@example.com"
                  value={email} onChange={e=>{setError('');setEmail(e.target.value);}}/>
              </div>
              <button className="auth-submit" type="submit" disabled={loading||!name.trim()}>
                {loading?'Creating…':'🌿 Create Account & Continue'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;